/* original source: https://github.com/lumpinif/shadcn-resizable-sidebar/blob/main/hooks/use-sidebar-resize.ts */
import { useStableCallback } from '@joyful/utils'
import { useEffect, useRef } from 'react'
export interface UseSidebarResizeProps {
  /**
   * Auto-collapse threshold as percentage of minResizeWidth
   * A value of 1.0 means the panel will collapse when dragged to minResizeWidth
   * A value of 0.5 means the panel will collapse when dragged to 50% of minResizeWidth
   * A value of 1.5 means the panel will collapse when dragged to 50% beyond minResizeWidth
   * Can be any positive number, not limited to the range 0.0-1.0
   */
  autoCollapseThreshold?: number

  /**
   * Current width of the panel
   */
  currentWidth: string

  /**
   * Direction of the resize handle
   * - 'left': Handle is on left side (for right-positioned panels)
   * - 'right': Handle is on right side (for left-positioned panels)
   */
  direction?: 'left' | 'right'

  /**
   * Whether to enable auto-collapse when dragged below threshold
   */
  enableAutoCollapse?: boolean

  /**
   * Whether to enable drag functionality
   */
  enableDrag?: boolean

  /**
   * Whether to enable toggle functionality
   */
  enableToggle?: boolean

  /**
   * Threshold to expand when dragging in opposite direction (0.0-1.0)
   * Percentage of distance needed to drag back to expand
   */
  expandThreshold?: number

  /**
   * Whether the panel is currently collapsed
   */
  isCollapsed?: boolean

  /**
   * Whether this is a nested sidebar (not at the edge of the screen)
   */
  isNested?: boolean

  /**
   * Maximum resize width
   */
  maxResizeWidth?: string

  /**
   * Minimum resize width
   */
  minResizeWidth?: string

  /**
   * Callback to update width when resizing
   */
  onResize: (width: string) => void

  /**
   * Callback to toggle panel visibility
   */
  onToggle?: () => void

  /**
   * Callback to update dragging rail state
   */
  setDraggingRail?: (isDragging: boolean) => void

  /**
   * Local storage key for persisting width
   */
  widthLocalStorageKey?: string
}

interface WidthUnit {
  unit: 'rem' | 'px'
  value: number
}

/**
 * Parse width string into value and unit
 */
function parseWidth(width: string): WidthUnit {
  const unit = width.endsWith('rem') ? 'rem' : 'px'
  const value = Number.parseFloat(width)
  return { unit, value }
}

/**
 * Convert any width to pixels for calculations
 */
function toPx(width: string): number {
  const { unit, value } = parseWidth(width)
  return unit === 'rem' ? value * 16 : value
}

/**
 * Format width value with unit
 */
function formatWidth(value: number, unit: 'rem' | 'px'): string {
  return `${unit === 'rem' ? value.toFixed(1) : Math.round(value)}${unit}`
}

function noop() {
  // do nothing
}

/**
 * A versatile hook for handling resizable sidebar (or inset) panels
 * Works for both sidebar (left side) and artifacts (right side) panels
 * Supports VS Code-like continuous drag to collapse/expand
 */
export function useSidebarResize({
  currentWidth,
  direction = 'right',
  enableDrag = true,
  enableToggle = true,
  expandThreshold = 0.2,
  isCollapsed = false,
  isNested = false,
  maxResizeWidth = '24rem', // Default to collapsing at minWidth + 50%
  minResizeWidth = '14rem',
  onResize,
  onToggle,
  setDraggingRail = noop,
  widthLocalStorageKey,
}: UseSidebarResizeProps) {
  // Refs for tracking drag state
  const dragRef = useRef<HTMLButtonElement>(null)
  const startWidthRef = useRef(0)
  const startXRef = useRef(0)
  const isDraggingRef = useRef(false)
  const isInteractingWithRailRef = useRef(false)
  const lastWidthRef = useRef(0)
  const lastLoggedWidthRef = useRef(0)
  const dragStartPointRef = useRef(0)
  const lastDragDirectionRef = useRef<'expand' | 'collapse' | null>(null)
  const lastTogglePointRef = useRef(0)
  const lastToggleWidthRef = useRef(0)
  const toggleCooldownRef = useRef(false)
  const lastToggleTimeRef = useRef(0)
  const dragDistanceFromToggleRef = useRef(0)
  const dragOffsetRef = useRef(0)
  const railRectRef = useRef<DOMRect | null>(null)

  // Performance optimization refs
  const animationFrameRef = useRef<number | null>(null)
  const lastEmittedWidthRef = useRef<string>('')
  const pendingWidthRef = useRef<number | null>(null)
  const throttleTimerRef = useRef<number | null>(null)
  const isUpdateScheduledRef = useRef<boolean>(false)

  // Memoize min/max width calculations for performance
  const minWidthPx = toPx(minResizeWidth)
  const maxWidthPx = toPx(maxResizeWidth)

  const widthUnit = parseWidth(currentWidth).unit

  const onResizeStable = useStableCallback(onResize)
  const canToggle = onToggle !== undefined
  const onToggleStable = useStableCallback(() => {
    onToggle?.()
  })
  const setDraggingRailStable = useStableCallback(setDraggingRail)

  // Helper function to determine if width is increasing based on direction and mouse movement
  const isIncreasingWidth = useStableCallback(
    (currentX: number, referenceX: number): boolean => {
      return direction === 'left'
        ? currentX < referenceX // For left-positioned handle, moving left increases width
        : currentX > referenceX // For right-positioned handle, moving right increases width
    },
  )

  // Helper function to calculate width based on mouse position and direction
  const calculateWidth = useStableCallback(
    (
      e: MouseEvent,
      initialX: number,
      initialWidth: number,
      currentRailRect: DOMRect | null,
    ): number => {
      if (isNested && currentRailRect) {
        // For nested sidebars, use the delta from start position for precise tracking
        const deltaX = e.clientX - initialX

        if (direction === 'left') {
          // For left-positioned handle (right panel)
          // Width increases as mouse moves left (negative deltaX)
          return initialWidth - deltaX
        }
        // For right-positioned handle (left panel)
        // Width increases as mouse moves right (positive deltaX)
        return initialWidth + deltaX
      }
      // For standard sidebars at window edges
      if (direction === 'left') {
        // For left-positioned handle (right panel)
        return window.innerWidth - e.clientX
      }
      // For right-positioned handle (left panel)
      return e.clientX
    },
  )

  // Throttled width update function (10ms intervals for smooth performance)
  const updateWidth = useStableCallback((widthPx: number) => {
    // Store the latest width value
    pendingWidthRef.current = widthPx

    // If an update is already scheduled, just update the pending value
    if (isUpdateScheduledRef.current) {
      return
    }

    // Schedule an update in 10ms
    isUpdateScheduledRef.current = true
    throttleTimerRef.current = window.setTimeout(() => {
      // Ensure we have a pending width to process
      if (pendingWidthRef.current === null) {
        return
      }

      const newWidth =
        widthUnit === 'rem'
          ? pendingWidthRef.current / 16
          : pendingWidthRef.current
      const formattedWidth = formatWidth(newWidth, widthUnit)

      // Only trigger state update if width actually changed
      if (formattedWidth !== lastEmittedWidthRef.current) {
        lastEmittedWidthRef.current = formattedWidth
        onResizeStable(formattedWidth)
      }

      // Reset scheduling flag
      isUpdateScheduledRef.current = false
      throttleTimerRef.current = null
    }, 10)
  })

  // Persist width to cookie if cookie name is provided
  const persistWidth = useStableCallback((width: string) => {
    if (widthLocalStorageKey !== undefined) {
      localStorage.setItem(widthLocalStorageKey, width)
    }
  })

  // Handle mouse down on resize handle
  const handleMouseDown = (e: React.MouseEvent) => {
    isInteractingWithRailRef.current = true

    if (!enableDrag) {
      return
    }

    // Store initial state
    const currentWidthPx = isCollapsed ? 0 : toPx(currentWidth)
    startWidthRef.current = currentWidthPx
    startXRef.current = e.clientX
    dragStartPointRef.current = e.clientX
    lastWidthRef.current = currentWidthPx
    lastLoggedWidthRef.current = currentWidthPx
    lastTogglePointRef.current = e.clientX
    lastToggleWidthRef.current = currentWidthPx
    lastDragDirectionRef.current = null
    toggleCooldownRef.current = false
    lastToggleTimeRef.current = 0
    dragDistanceFromToggleRef.current = 0

    // Reset drag offset
    dragOffsetRef.current = 0

    // Store the rail element's position for nested sidebars - cache it once
    if (isNested && dragRef.current) {
      railRectRef.current = dragRef.current.getBoundingClientRect()
    } else {
      railRectRef.current = null
    }

    // Reset performance tracking
    lastEmittedWidthRef.current = ''
    pendingWidthRef.current = null

    e.preventDefault()
  }

  // Handle mouse movement and resizing
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isInteractingWithRailRef.current) {
        return
      }

      const deltaX = Math.abs(e.clientX - startXRef.current)
      if (!isDraggingRef.current && deltaX > 5) {
        isDraggingRef.current = true
        setDraggingRailStable(true)
      }

      if (isDraggingRef.current) {
        // Use cached rail rect to avoid DOM queries on every move
        const currentRailRect = railRectRef.current

        // Determine current drag direction
        const currentDragDirection = isIncreasingWidth(
          e.clientX,
          lastTogglePointRef.current,
        )
          ? 'expand'
          : 'collapse'

        // Update direction tracking
        if (lastDragDirectionRef.current !== currentDragDirection) {
          lastDragDirectionRef.current = currentDragDirection
        }

        // Calculate distance from last toggle point
        dragDistanceFromToggleRef.current = Math.abs(
          e.clientX - lastTogglePointRef.current,
        )

        // Check for toggle cooldown (prevent rapid toggling)
        const now = Date.now()
        if (
          toggleCooldownRef.current &&
          now - lastToggleTimeRef.current > 200
        ) {
          toggleCooldownRef.current = false
        }

        // Handle toggling between collapsed and expanded states
        if (!toggleCooldownRef.current) {
          // Handle expanding when collapsed
          if (
            canToggle &&
            isCollapsed &&
            currentDragDirection === 'expand' &&
            dragDistanceFromToggleRef.current > minWidthPx * expandThreshold
          ) {
            onToggleStable() // Expand

            // Calculate initial width based on exact mouse position
            const initialWidth = calculateWidth(
              e,
              startXRef.current,
              startWidthRef.current,
              currentRailRect,
            )

            // Clamp to min/max
            const clampedWidth = Math.max(
              minWidthPx,
              Math.min(maxWidthPx, initialWidth),
            )

            // Use optimized update function
            updateWidth(clampedWidth)

            lastTogglePointRef.current = e.clientX
            lastToggleWidthRef.current = clampedWidth
            toggleCooldownRef.current = true
            lastToggleTimeRef.current = now
            return
          }
        }

        // Skip width calculations if panel is collapsed
        if (isCollapsed) {
          return
        }

        // Calculate new width based on mouse position and drag direction
        const newWidthPx = calculateWidth(
          e,
          startXRef.current,
          startWidthRef.current,
          currentRailRect,
        )

        // Clamp width between min and max
        const clampedWidthPx = Math.max(
          minWidthPx,
          Math.min(maxWidthPx, newWidthPx),
        )

        // Use optimized update function for smooth performance
        updateWidth(clampedWidthPx)

        // Update last width
        lastWidthRef.current = clampedWidthPx
      }
    }

    const handleMouseUp = () => {
      if (!isInteractingWithRailRef.current) {
        return
      }

      // Cancel any pending animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }

      // Cancel any pending throttled update and execute final update immediately
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current)
        throttleTimerRef.current = null
        isUpdateScheduledRef.current = false

        // Execute final update with the latest pending width
        if (pendingWidthRef.current !== null) {
          const newWidth =
            widthUnit === 'rem'
              ? pendingWidthRef.current / 16
              : pendingWidthRef.current
          const formattedWidth = formatWidth(newWidth, widthUnit)
          lastEmittedWidthRef.current = formattedWidth
          onResizeStable(formattedWidth)
        }
      }

      // Persist the final width when drag is complete
      if (lastEmittedWidthRef.current) {
        persistWidth(lastEmittedWidthRef.current)
      }

      // Handle click (not drag) behavior
      if (!isDraggingRef.current && canToggle && enableToggle) {
        onToggleStable()
      }

      // Reset all state
      isDraggingRef.current = false
      isInteractingWithRailRef.current = false
      lastWidthRef.current = 0
      lastLoggedWidthRef.current = 0
      lastDragDirectionRef.current = null
      lastTogglePointRef.current = 0
      lastToggleWidthRef.current = 0
      toggleCooldownRef.current = false
      lastToggleTimeRef.current = 0
      dragDistanceFromToggleRef.current = 0
      dragOffsetRef.current = 0
      railRectRef.current = null
      lastEmittedWidthRef.current = ''
      pendingWidthRef.current = null
      setDraggingRailStable(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [
    onResizeStable,
    canToggle,
    isCollapsed,
    currentWidth,
    persistWidth,
    setDraggingRailStable,
    minWidthPx,
    maxWidthPx,
    isIncreasingWidth,
    calculateWidth,
    isNested,
    expandThreshold,
    enableToggle,
    updateWidth,
  ])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [])

  return {
    dragRef,
    handleMouseDown,
    isDragging: isDraggingRef,
  }
}
